import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { DekoxIcon } from "src/components";
import { TGroupNode } from "../../configurator-types";

// Total leaf-item count under a node, whether it's a direct leaf or nests further branches
function countGroupNodeItems<TItem>(node: TGroupNode<TItem>): number {
  return "items" in node ? node.items.length : node.children.reduce((sum, child) => sum + countGroupNodeItems(child), 0);
}

function buildGroupNodePath(parentPath: string, key: string): string {
  return parentPath ? `${parentPath}›${key}` : key;
}

// Every path in the tree, branch and leaf alike — used to fill the expanded-paths Set for Expand All
export function collectAllGroupNodePaths<TItem>(nodes: TGroupNode<TItem>[], parentPath = ""): string[] {
  return nodes.flatMap(node => {
    const path = buildGroupNodePath(parentPath, node.key);
    return "items" in node ? [path] : [path, ...collectAllGroupNodePaths(node.children, path)];
  });
}

type Props<TItem> = {
  node: TGroupNode<TItem>;
  parentPath: string;
  expandedPaths: Set<string>;
  onToggle: (path: string, expanded: boolean) => void;
  renderItems: (items: TItem[]) => ReactNode;
  itemCountLabel: (count: number) => string;
};

// Recursive Accordion renderer for a TGroupNode tree — a branch nests further GroupNodes, a leaf
// renders the caller's own item list (ProductConfiguration / Configuration), indented by depth.
export function GroupNode<TItem>({ node, parentPath, expandedPaths, onToggle, renderItems, itemCountLabel }: Props<TItem>) {
  const theme = useTheme();
  const path = buildGroupNodePath(parentPath, node.key);
  const count = countGroupNodeItems(node);

  return (
    <Accordion
      expanded={expandedPaths.has(path)}
      onChange={(_, expanded) => onToggle(path, expanded)}
      // Unmount cards when the node is collapsed — same reason as the pre-hierarchy grouping had
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        boxShadow: "none",
        border: `1px solid ${theme.palette.customColors.grey}`,
        borderRadius: 1,
        overflow: "hidden",
        ml: node.depth * 2,
        "&.Mui-expanded": {
          margin: 0,
          ml: node.depth * 2,
          // MUI wraps AccordionSummary inside its own "& > .MuiAccordion-heading" div (not a direct
          // child itself), so scope through that wrapper — nested GroupNodes/Configuration accordions
          // live inside the sibling .MuiCollapse-root branch instead, so their icons are unaffected
          "& > .MuiAccordion-heading .MuiSvgIcon-root[data-testid='KeyboardArrowDownIcon']": { transform: "rotate(180deg)" }
        },
        ":last-of-type": { borderRadius: 1 }
      }}
    >
      <AccordionSummary
        sx={{
          overflow: "hidden",
          minHeight: 0,
          py: 1,
          userSelect: "none",
          ".MuiAccordionSummary-content": { overflow: "hidden", my: 0 }
        }}
      >
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" overflow="hidden">
          <Stack flex={1} direction="row" alignItems="center" overflow="hidden">
            <DekoxIcon icon="arrow-down" />
            <Typography variant="body2" fontWeight="500" ml={1}>
              {node.label}
            </Typography>
            <Typography variant="caption" ml={3} mr={2}>
              {itemCountLabel(count)}
            </Typography>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {"items" in node ? (
          renderItems(node.items)
        ) : (
          <Stack gap={1}>
            {node.children.map(child => (
              <GroupNode key={child.key} node={child} parentPath={path} expandedPaths={expandedPaths} onToggle={onToggle} renderItems={renderItems} itemCountLabel={itemCountLabel} />
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
